import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { TagInput } from '@wit-support/ui';
import { CountryLabel } from './country-label.js';
import { cn } from './lib/cn.js';
import { ALL_COUNTRY_CODES, countryName, countryNameWithCode, parseCountryList, searchCountries, } from './lib/country.js';
const DELIMITER = /[,;\n\t]/;
/**
 * Выбор стран: поиск по названию (рус./англ.) и коду, чипы «🇩🇪 Германия DE».
 * Хранит и отдаёт ISO-коды — контракт полей не меняется, меняется ввод.
 *
 * Вставка списка кодов работает, как работал ручной ввод: «DE, FR, IT»
 * (через запятую, точку с запятой, перевод строки) или «DE FR IT» — узнанное
 * сразу становится чипами, неузнанное остаётся в поле, чтобы его было видно.
 * Названия тоже узнаются: «Германия, Франция».
 */
export function CountryPicker({ value, onChange, disabled = false, maxCountries = ALL_COUNTRY_CODES.length, placeholder = 'Страна или код…', 'aria-label': ariaLabel = 'Страны', className, }) {
    const [query, setQuery] = React.useState('');
    /** Неузнанное из последней вставки — подписью под полем, а не молча в никуда. */
    const [unrecognized, setUnrecognized] = React.useState([]);
    /** Узнанное, но не влезшее в `maxCountries`, — тоже подписью, а не молча. */
    const [overflow, setOverflow] = React.useState([]);
    const suggestions = React.useMemo(() => searchCountries(query).map((code) => ({ id: code, name: countryName(code) })), [query]);
    const addCodes = (codes) => {
        const next = [...value];
        const dropped = [];
        for (const c of codes) {
            if (next.includes(c))
                continue;
            if (next.length >= maxCountries)
                dropped.push(c);
            else
                next.push(c);
        }
        setOverflow(dropped);
        if (next.length !== value.length)
            onChange(next);
    };
    // Ручной выбор или снятие чипа — подписи о прошлой вставке устарели.
    const handleChange = (next) => {
        setUnrecognized([]);
        setOverflow([]);
        onChange(next);
    };
    const handleQuery = (q) => {
        if (DELIMITER.test(q)) {
            // всё до последнего разделителя — готовые токены, после — ещё набирается
            const cut = Math.max(q.lastIndexOf(','), q.lastIndexOf(';'), q.lastIndexOf('\n'), q.lastIndexOf('\t'));
            const { codes, rest } = parseCountryList(q.slice(0, cut));
            addCodes(codes);
            setUnrecognized(rest);
            setQuery(q.slice(cut + 1).trimStart());
            return;
        }
        // «DE FR IT» без запятых — только если узнались все слова и их больше одного
        const words = q.trim().split(/\s+/);
        if (words.length > 1) {
            const { codes, rest } = parseCountryList(q);
            if (rest.length === 0 && codes.length === words.length) {
                addCodes(codes);
                setUnrecognized([]);
                setQuery('');
                return;
            }
        }
        setQuery(q);
    };
    return (_jsxs("div", { className: cn('flex flex-col gap-1', className), children: [_jsx(TagInput, { identity: "id", allowCreate: false, value: [...value], getLabel: countryNameWithCode, renderValue: (code) => _jsx(CountryLabel, { code: code }), renderSuggestion: (s) => _jsx(CountryLabel, { code: s.id }), suggestions: suggestions, query: query, onQueryChange: handleQuery, onChange: handleChange, disabled: disabled, maxTags: maxCountries, placeholder: placeholder, emptyLabel: "\u0421\u0442\u0440\u0430\u043D\u0430 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430", "aria-label": ariaLabel }), unrecognized.length > 0 ? (_jsxs("p", { className: "text-xs text-[var(--color-state-warning-fg)]", role: "status", children: ["\u041D\u0435 \u0443\u0437\u043D\u0430\u043D\u043E: ", unrecognized.join(', ')] })) : null, overflow.length > 0 ? (_jsxs("p", { className: "text-xs text-[var(--color-state-warning-fg)]", role: "status", children: ["\u041D\u0435 \u0431\u043E\u043B\u044C\u0448\u0435 ", maxCountries, " \u0441\u0442\u0440\u0430\u043D \u2014 \u043D\u0435 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u044B:", ' ', overflow.map(countryNameWithCode).join(', ')] })) : null] }));
}
CountryPicker.displayName = 'CountryPicker';
//# sourceMappingURL=country-picker.js.map